//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import ActionButton from '@components/atoms/action-button/ActionButton';
import { Caption } from '@components/atoms/caption/Caption';
import Icon from '@components/atoms/icon/Icon';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodySecondaryText, BodyText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Product, ProductType } from '@models/IItemMetadata';
import { Mixins } from '@theme/mixins';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { getIsBvProduct } from '@uia/products/utils';
import { isDefined } from '@utils/filterPredicates';

const CRAWL_TYPE: ProductType[] = ['Crawl', 'UpdateCrawl'];

export interface ProductTagItemProps {
  onEditProductTag: (productTag: Product) => void;
  onRemoveProductTag: (productTag: Product) => void;
  productTag: Product;
}

export const ProductTagItem = ({
  onEditProductTag,
  onRemoveProductTag,
  productTag,
}: ProductTagItemProps) => {
  const {
    hasCatalogConnected: isCatalogConnected,
    isProcessingProductTagsMutation,
    isProductEditingFeatureDisabled,
  } = useProductTaggingContext();
  const { imageUrl, name, position, productType, spatialTag } = productTag;
  const [isHovering, setIsHovering] = useState(false);

  const isBvProduct = getIsBvProduct(productTag.productStyleId);

  return (
    <ProductTag
      onMouseLeave={() => setIsHovering(false)}
      onMouseOver={() => setIsHovering(true)}
    >
      <ProductDetails>
        {isDefined(imageUrl) && imageUrl.length > 0 ? (
          <>
            {isDefined(spatialTag) && isDefined(position) ? (
              <div>
                <SpatialTagNumber>{position}</SpatialTagNumber>
                <ProductImage alt={name ?? 'Product image'} src={imageUrl} />
              </div>
            ) : (
              <ProductImage alt={name ?? 'Product image'} src={imageUrl} />
            )}
          </>
        ) : (
          <ProductImage alt={name ?? 'Product image'} src={ProductImage} />
        )}
        <ContentContainer>
          <ProductName ellipsize={2} title={name ?? 'Unnamed Product'}>
            {name ?? 'Unnamed Product'}
          </ProductName>
          {isCatalogConnected && !isBvProduct && (
            <BodySecondaryText>
              {(() => {
                if (productTag.isExpired) {
                  return 'Removed from catalog';
                } else if (
                  productTag.isAvailable &&
                  isDefined(productTag.price)
                ) {
                  return productTag.price;
                } else if (
                  isDefined(productType) &&
                  CRAWL_TYPE.includes(productType)
                ) {
                  return '';
                } else {
                  return 'Out of stock';
                }
              })()}
            </BodySecondaryText>
          )}
          <ProductUrl
            caption={productTag.destinationUrl}
            maxLineCount={1}
            shouldDecorateLinks={false}
          />
        </ContentContainer>
      </ProductDetails>
      <ActionsContainer>
        {isHovering && !isProcessingProductTagsMutation && (
          <Actions>
            {!isProductEditingFeatureDisabled && (
              <ActionButton
                aria-label='Edit product'
                iconName='edit'
                onClick={() => onEditProductTag(productTag)}
                size='extra-small'
                trackingId={UIATrackingIds.PRODUCTS_PRODUCT_TAGS_EDIT}
              />
            )}
            <ActionButton
              aria-label='Delete product'
              iconName='delete'
              onClick={() => onRemoveProductTag(productTag)}
              size='extra-small'
              trackingId={UIATrackingIds.PRODUCTS_PRODUCT_TAGS_REMOVE}
            />
          </Actions>
        )}
        {isCatalogConnected &&
          isDefined(productType) &&
          CRAWL_TYPE.includes(productType) && (
            <TooltipContainer>
              <SmallTooltip
                content="This tag was added via URL (not from your catalog), which can impact your consumer's experiences."
                target={<StyledIcon name='info' />}
              />
            </TooltipContainer>
          )}
      </ActionsContainer>
    </ProductTag>
  );
};

const ProductTag = styled.div(
  ({ theme }) => `
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: start;
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-radius: ${theme.borderRadii[2]};
    &:hover {
      background-color: ${theme.colors.gray00};
    }
  `,
);

const ProductDetails = styled.div(
  ({ theme }) => `
  display: flex;
  width: 100%;
  padding: ${theme.spacings[2]};
`,
);

const ProductImage = styled(ImageWithErrorHandling)(
  ({ theme }) => css`
    width: calc(${theme.spacings[7]} + ${theme.spacings[5]});
    aspect-ratio: 0.6;
    object-fit: cover;
  `,
);

const SpatialTagNumber = styled.div(
  ({ theme }) => `
    background: ${theme.colors.white};
    border-radius: 50%;
    border: ${theme.borderWidths[1]} solid ${theme.colors.gray5};
    height: ${theme.spacings[5]};
    margin: ${theme.spacings[1]};
    padding-top: ${theme.spacings[1]};
    position: absolute;
    text-align: center;
    width: ${theme.spacings[5]};
  `,
);

const ContentContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
    padding: 0 ${theme.spacings[5]} 0 ${theme.spacings[2]};
  `,
);

const ProductName = styled(BodyText)(
  ({ theme }) => `
  padding-right: ${theme.spacings[3]};
`,
);

const ProductUrl = styled(Caption)`
  ${Mixins.Text.BodySecondary}
  word-break: break-all;
`;

const ActionsContainer = styled.div(
  ({ theme }) => `
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: end;
    padding: ${theme.spacings[2]};
  `,
);

const Actions = styled.div`
  display: flex;
  justify-content: end;
  align-items: start;
`;

const TooltipContainer = styled.div`
  margin-top: auto;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
  margin-top: auto;
  color: ${theme.colors.aliases.bodySecondary};
`,
);
